// How records appear in lists: open rows (due date), upcoming rows (fiscal-year window), and the
// brief's larger "pick" cards. A row opens the detail panel.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { api } from "../api/client";
import type { FundingChip, NoticeItem, RelatedNotice } from "../api/types";
import { useSelectedKey, useUI } from "../app/context";
import { companyName, days, dueText, fmt, joinWords, lineRef, money, shortDate, today } from "../lib/format";
import { Badge } from "./Badge";

export function DuePill({ n }: { n: Pick<NoticeItem, "deadline"> }) {
  const d = days(n.deadline);
  return <span className={`due ${d !== null && d >= 0 && d <= 3 ? "urgent" : ""}`}>{dueText(n)}</span>;
}

export function Fit({ n }: { n: Pick<NoticeItem, "fit_word"> }) {
  return n.fit_word ? <span className={`fit ${n.fit_word === "Strong fit" ? "strong" : ""}`}>{n.fit_word}</span> : null;
}

export const SoleChip = () => (
  <span className="chip warn" title="A notice of intent to award to a named vendor">
    Intent to sole source
  </span>
);

/** Who won the solicitation, once an award is known. */
export function WonChip({ a }: { a: NoticeItem["awarded"] }) {
  if (!a) return null;
  return (
    <span className="fund won" title={`Awarded${a.date ? " " + a.date : ""}${a.amount != null ? ", " + money(a.amount / 1e6) : ""}`}>
      Won by {companyName(a.vendor)}
      {a.count > 1 ? ` + ${a.count - 1}` : ""}
    </span>
  );
}

/** The strongest budget link: the program element and how its funding changed. */
export function FundChip({ f }: { f: FundingChip | null | undefined }) {
  if (!f) return null;
  const pct =
    f.status === "new"
      ? "new start"
      : f.status === "ending"
        ? "ending"
        : f.change == null
          ? ""
          : `${f.change > 0 ? "▲" : "▼"}${Math.abs(Math.round(f.change * 100))}%`;
  return (
    <span className={`fund ${f.strength}`} title={`${f.strength === "possible" ? "Possibly funded by" : "Funded by"} ${f.title} (${f.service})`}>
      {lineRef(f)}
      {pct ? " " + pct : ""}
      {f.strength === "possible" ? " · possible" : ""}
    </span>
  );
}

function Meta({ parts }: { parts: ReactNode[] }) {
  const shown = parts.filter((p) => p !== null && p !== undefined && p !== false && p !== "");
  return (
    <div className="m">
      {shown.map((p, i) => (
        <span key={i}>
          {i ? " " : ""}
          {p}
        </span>
      ))}
    </div>
  );
}

function useOpen(n: NoticeItem) {
  const { openPanel } = useUI();
  const open = () => openPanel({ kind: "notice", key: n.key, feedId: n.feed?.id ?? null });
  return {
    onClick: open,
    onKeyDown: (e: KeyboardEvent) => {
      if (e.key === "Enter") open();
    },
  };
}

/** "+N": the same procurement's other records (amendments, other sources), expanded under the row. */
export function RelatedToggle({ n, open, onToggle }: { n: { related?: RelatedNotice[] }; open: boolean; onToggle: () => void }) {
  const count = n.related?.length ?? 0;
  if (!count) return null;
  return (
    <button
      type="button"
      className="related-toggle"
      aria-expanded={open}
      title={`${count} more notice${count === 1 ? "" : "s"} for this procurement: amendments and other sources`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {open ? "−" : "+"}
      {count}
    </button>
  );
}

export function RelatedList({ n, feedId }: { n: { related?: RelatedNotice[] }; feedId: number | null }) {
  const { openPanel } = useUI();
  return (
    <div className="related-rows">
      {(n.related ?? []).map((r) => (
        <button key={r.key} type="button" className="related-row" onClick={() => openPanel({ kind: "notice", key: r.key, feedId })}>
          <span className="t">{r.title}</span>
          <span className="m">
            {[
              r.kind,
              r.source_label,
              r.published ? `posted ${fmt(r.published, { month: "short", day: "numeric", year: "numeric" })}` : "",
              r.deadline ? `due ${fmt(r.deadline, { month: "short", day: "numeric", year: "numeric" })}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </button>
      ))}
    </div>
  );
}

export function NoticeRow({ n, inBrief, since }: { n: NoticeItem; inBrief?: boolean; since?: string }) {
  const selected = useSelectedKey() === n.key;
  const isNew = !inBrief && since && n.first_seen >= since;
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div className="row" data-key={n.key} data-feed={n.feed?.id} role="button" tabIndex={0} aria-selected={selected} {...useOpen(n)}>
        <Badge agency={n.agency} small />
        <div style={{ minWidth: 0 }}>
          <div className="t">{n.title}</div>
          <Meta
            parts={[
              isNew && <span className="new">New</span>,
              n.reissued && <span className="new">Reissued</span>,
              n.buyer,
              n.work_label && <span className="shape-tag">{n.work_label}</span>,
              n.path_label || n.kind,
              n.who_label && <span className="shape-tag">{n.who_label}</span>,
              n.named && <span className="shape-tag named" title={`Buys ${n.named}: licenses, renewals or support`}>Named product</span>,
              n.source_label,
              n.mark && <span className="mark">{n.mark === "pursue" ? "Pursuing" : "Watching"}</span>,
              n.sole_source && "Intent to sole source",
              <WonChip a={n.awarded} />,
              <FundChip f={n.funding} />,
              inBrief && n.also ? `also in ${n.also} other feed${n.also > 1 ? "s" : ""}` : null,
              <RelatedToggle n={n} open={expanded} onToggle={() => setExpanded(!expanded)} />,
            ]}
          />
        </div>
        <div>
          <DuePill n={n} />
        </div>
        <div className="fitcol">
          <Fit n={n} />
        </div>
      </div>
      {expanded ? <RelatedList n={n} feedId={n.feed?.id ?? null} /> : null}
    </>
  );
}

/** An upcoming record: its fiscal-year window stands where an open record shows its due date. */
function EstimatePill({ n }: { n: NoticeItem }) {
  if (n.pages) {
    const c = n.page_count ?? 0;
    return (
      <span className="due est" title={`${c} page${c === 1 ? "" : "s"} of this document match`}>
        {c} page{c === 1 ? "" : "s"}
      </span>
    );
  }
  if (n.horizon === "context") return <span className="due est">{fmt(n.published)}</span>;
  if (n.horizon === "withdrawn") return <span className="due est past" title={n.withdrawn ?? ""}>No longer forecast</span>;
  const w = n.window;
  if (!w) return <span className="due est">Date not stated</span>;
  return (
    <span
      className={`due est ${w.basis === "released" ? "released" : w.past ? "past" : ""}`}
      title={w.text + (w.source_text ? ` (source: “${w.source_text}”)` : "")}
    >
      {w.label}
    </span>
  );
}

export function UpcomingRow({ n, inBrief }: { n: NoticeItem; inBrief?: boolean }) {
  const selected = useSelectedKey() === n.key;
  const what = n.pages
    ? `Briefing or roadmap · ${n.pages
        .map((p) => (p.page ? "p. " + p.page : ""))
        .filter(Boolean)
        .slice(0, 6)
        .join(", ")}${(n.page_count ?? 0) > 6 ? "…" : ""}`
    : n.window
      ? n.window.text
      : n.horizon === "context"
        ? "News"
        : n.kind;
  return (
    <div className="row upcoming" data-key={n.key} data-feed={n.feed?.id} role="button" tabIndex={0} aria-selected={selected} {...useOpen(n)}>
      <Badge agency={n.agency} small />
      <div style={{ minWidth: 0 }}>
        <div className="t">{n.title}</div>
        <Meta
          parts={[
            n.roadmaps ? (
              <span className="fund roadmap" title={`${n.roadmaps} briefing or roadmap page${n.roadmaps === 1 ? "" : "s"} name this program: open it to read what they say`}>
                In roadmaps
              </span>
            ) : null,
            n.buyer,
            what,
            n.source_label,
            <FundChip f={n.funding} />,
            n.mark && <span className="mark">{n.mark === "pursue" ? "Pursuing" : "Watching"}</span>,
            inBrief && n.feed ? `in ${n.feed.name}` : null,
          ]}
        />
      </div>
      <div>
        <EstimatePill n={n} />
      </div>
      <div className="fitcol">
        <Fit n={n} />
      </div>
    </div>
  );
}

/** "Oct 27–28, 2026", "Oct 30 – Nov 2, 2026", "Sep 30": an event's days. */
export function eventWhen(e: { start: string | null; end: string | null }): string {
  if (!e.start) return "Date not stated";
  if (!e.end || e.end === e.start) return shortDate(e.start, { weekday: "short", month: "short", day: "numeric" });
  const a = new Date(e.start + "T00:00:00");
  const b = new Date(e.end + "T00:00:00");
  if (a.getFullYear() !== b.getFullYear()) return `${fmt(e.start)}, ${a.getFullYear()} – ${fmt(e.end)}, ${b.getFullYear()}`;
  const sameMonth = a.getMonth() === b.getMonth();
  const year = a.getFullYear() === today().getFullYear() ? "" : `, ${a.getFullYear()}`;
  return sameMonth ? `${fmt(e.start)}–${b.getDate()}${year}` : `${fmt(e.start)} – ${fmt(e.end)}${year}`;
}

/** A proposers day, industry day or the like: what kind, when and where, and whether its opportunity is known. */
export function EventRow({ n }: { n: NoticeItem }) {
  const selected = useSelectedKey() === n.key;
  const e = n.event;
  const soon = e?.start ? (days(e.start) ?? 99) <= 7 : false;
  const closes = e?.registration_closes && (days(e.registration_closes) ?? -1) >= 0 ? e.registration_closes : null;
  return (
    <div className="row upcoming event" data-key={n.key} data-feed={n.feed?.id} role="button" tabIndex={0} aria-selected={selected} {...useOpen(n)}>
      <Badge agency={n.agency} small />
      <div style={{ minWidth: 0 }}>
        <div className="t">{n.title}</div>
        <Meta
          parts={[
            <span className="fund event-kind">{e?.label ?? n.kind}</span>,
            e?.host && e.host !== "other" ? e.host_label : n.buyer,
            e?.location && e.location.toLowerCase() !== e.host_label.toLowerCase() ? e.location : null,
            closes ? <span className={(days(closes) ?? 9) <= 3 ? "warn-text" : ""}>Register by {shortDate(closes)}</span> : null,
            n.events ? <span className="fund roadmap">Opportunity posted</span> : null,
            n.reasons?.length ? <span className="muted-why">{n.reasons.join(" · ")}</span> : n.source_label,
          ]}
        />
      </div>
      <div>
        <span className={`due ${soon ? "urgent" : ""}`}>{e ? eventWhen(e) : n.kind}</span>
      </div>
      <div className="fitcol">
        <Fit n={n} />
      </div>
    </div>
  );
}

/** Follow an organization's events (and those under it) on the Events page. */
export function FollowHost({ id, label }: { id: string; label: string }) {
  const client = useQueryClient();
  const { showToast } = useUI();
  const follows = useQuery({ queryKey: ["events", "follows"], queryFn: () => api<{ follows: string[] }>("/api/events/follows") });
  const on = follows.data?.follows.includes(id) ?? false;
  const toggle = async (ev: MouseEvent) => {
    ev.stopPropagation();
    await api(`/api/events/follows/${encodeURIComponent(id)}`, { method: on ? "DELETE" : "PUT" });
    await client.invalidateQueries({ queryKey: ["events"] });
    showToast(on ? `You no longer follow ${label}'s events.` : `${label}'s events now show under Following.`);
  };
  return (
    <button className="btn follow-host" aria-pressed={on} disabled={follows.isPending} onClick={toggle}>
      {on ? "Following" : "Follow"}
    </button>
  );
}

/** A contract that ends soon: who holds it, what it's worth, when it ends, and its follow-on if posted. */
export function EndingRow({ n }: { n: NoticeItem }) {
  const selected = useSelectedKey() === n.key;
  const { openPanel } = useUI();
  const e = n.ending!;
  const left = days(e.end);
  return (
    <div className="row upcoming" data-key={n.key} data-feed={n.feed?.id} role="button" tabIndex={0} aria-selected={selected} {...useOpen(n)}>
      <Badge agency={n.agency} small />
      <div style={{ minWidth: 0 }}>
        <div className="t">{n.title.replace(/\s*\(ends [^)]*\)$/, "")}</div>
        <Meta
          parts={[
            n.buyer,
            e.incumbent && <span className="shape-tag">Held by {companyName(e.incumbent)}</span>,
            e.value,
            e.contract &&
              `${e.contract}${e.umbrella ? ` under ${e.umbrella}${e.umbrella_ends ? ` (runs to ${fmt(e.umbrella_ends, { month: "short", year: "numeric" })})` : ""}` : ""}`,
            n.source_label,
            e.follow_on && (
              <button
                className="link-btn"
                onClick={(ev) => {
                  ev.stopPropagation();
                  openPanel({ kind: "notice", key: e.follow_on!.key, feedId: n.feed?.id ?? null });
                }}
              >
                Follow-on: {e.follow_on.kind} “{e.follow_on.title.slice(0, 60)}”{e.follow_on.deadline ? `, due ${fmt(e.follow_on.deadline)}` : ""} →
              </button>
            ),
          ]}
        />
      </div>
      <div>
        <span className={`due ${left !== null && left >= 0 && left <= 90 ? "urgent" : ""}`}>
          {left !== null && left < 0 ? "Ended" : "Ends"} {fmt(e.end, { month: "short", day: "numeric", year: "numeric" })}
        </span>
      </div>
      <div className="fitcol">
        <Fit n={n} />
      </div>
    </div>
  );
}

export function PickCard({ n }: { n: NoticeItem & { feed: NonNullable<NoticeItem["feed"]> } }) {
  const { openPanel } = useUI();
  const f = n.feed;
  const terms = n.why_terms?.length ? n.why_terms : n.terms;
  const shape = [n.work_label, n.path_label || n.kind, n.who_label].filter(Boolean).join(", ");
  return (
    <article className="pick" data-key={n.key} data-feed={f.id} onClick={() => openPanel({ kind: "notice", key: n.key, feedId: f.id })}>
      <Badge agency={n.agency} />
      <div>
        <h3>{n.title}</h3>
        <div className="by">
          {n.buyer}, found on {n.source_label}, in {f.mine ? "your" : `${f.owner_name.split(" ")[0]}’s`} {f.name} feed
        </div>
        <p className="why">
          {terms.length ? (
            <>
              Mentions <Terms terms={terms} />.{" "}
            </>
          ) : null}
          {shape}.{n.sole_source ? <> {<SoleChip />}</> : null}
        </p>
      </div>
      <aside>
        <DuePill n={n} />
        <Fit n={n} />
      </aside>
    </article>
  );
}

export function Terms({ terms }: { terms: string[] }) {
  return (
    <>
      {joinWords(
        terms.map((t, i) => (
          <em key={i} className="term">
            {t}
          </em>
        )),
      )}
    </>
  );
}
